// Loaded by sections/cart-drawer.liquid and sections/main-cart.liquid next to cart.js.
//
// 1. A /discount/CODE link (Facebook ads) leaves a `discount_code` cookie that Shopify re-applies
//    on every checkout start, overriding whatever code the customer set in the cart. Once the
//    customer changes codes in the cart, the cart becomes the source of truth and the cookie goes.
// 2. Replaces the submit handler of the theme's <form is="cart-discount"> (assets/cart.js) so a new
//    code that cannot combine with the codes already in the cart replaces them instead of failing.
//    Removing a code (<button is="cart-discount-remove">) stays with the theme.
(() => {
  const clearAdCookie = () => {
    document.cookie = 'discount_code=; Max-Age=0; path=/';
  };

  const sameCode = (a, b) => a.toLowerCase() === b.toLowerCase();
  const isApplicable = (state, code) => state.discount_codes.some((c) => sameCode(c.code, code) && c.applicable);

  const getCart = async () => (await fetch(`${FoxTheme.routes.cart_url}.js`)).json();

  const updateDiscounts = async (codes) => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    const response = await fetch(`${FoxTheme.routes.cart_update_url}.js`, {
      ...FoxTheme.utils.fetchConfig(),
      body: JSON.stringify({ discount: codes.join(','), sections, sections_url: window.location.pathname }),
    });
    const state = await response.json();
    if (!response.ok || !Array.isArray(state.discount_codes)) {
      throw new Error(state.description || state.message || FoxTheme.cartStrings.applyDiscountError);
    }
    return state;
  };

  const setLoading = (el, loading) => {
    if (loading) el.setAttribute('aria-disabled', 'true');
    else el.removeAttribute('aria-disabled');
    el.classList.toggle('btn--loading', loading);
  };

  const applyCode = async (form) => {
    const newCode = form.couponEl.value.trim();
    if (!newCode || form.submitEl.getAttribute('aria-disabled') === 'true') return;

    form.displayFormErrors();
    setLoading(form.submitEl, true);

    try {
      const cartCodes = (await getCart()).discount_codes;
      if (cartCodes.some((c) => c.applicable && sameCode(c.code, newCode))) {
        form.displayFormErrors(FoxTheme.cartStrings.duplicateDiscountError);
        return;
      }

      // The cart can hold the typed code as inapplicable (blocked by a non-combinable one) —
      // that is not a duplicate, the customer is retrying it.
      const currentCodes = cartCodes.map((c) => c.code).filter((code) => !sameCode(code, newCode));

      let state = await updateDiscounts([...currentCodes, newCode]);

      // Non-combinable codes: Shopify keeps one and marks the other inapplicable. The customer
      // just typed the new one, so let it replace the old ones instead of failing.
      if (!isApplicable(state, newCode) && currentCodes.length) {
        state = await updateDiscounts([newCode]);
      }

      if (!isApplicable(state, newCode)) {
        if (cartCodes.length) await updateDiscounts(cartCodes.map((c) => c.code));
        form.couponEl.value = '';
        form.displayFormErrors(FoxTheme.cartStrings.applyDiscountError);
        return;
      }

      clearAdCookie();
      form.couponEl.value = '';
      form.cartAddonDrawer?.hide();
      const cart = await getCart();
      cart.sections = state.sections;
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    } catch (error) {
      console.error(error);
      form.displayFormErrors(error.message || FoxTheme.cartStrings.applyDiscountError);
    } finally {
      setLoading(form.submitEl, false);
    }
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest?.('form[is="cart-discount"]');
      if (!form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      applyCode(form);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest?.('button[is="cart-discount-remove"]')) clearAdCookie();
    },
    true
  );
})();
